import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, Pause, Play } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

interface Story {
  id: string;
  user_id: string;
  content_url: string;
  content_type: string;
  caption: string | null;
  created_at: string;
  expires_at: string;
  profiles?: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
  is_viewed?: boolean;
}

interface UserStories {
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  stories: Story[];
  has_unviewed: boolean;
}

interface StoryViewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userStories: UserStories;
  startIndex: number;
  onStoryViewed: (storyId: string) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const StoryViewModal = ({
  open,
  onOpenChange,
  userStories,
  startIndex,
  onStoryViewed,
  onNext,
  onPrevious
}: StoryViewModalProps) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  const progressIntervalRef = useRef<NodeJS.Timeout>();
  const videoRef = useRef<HTMLVideoElement>(null);

  const currentStory = userStories.stories[currentIndex];
  const storyDuration = 5000; // 5 seconds for images

  useEffect(() => {
    setCurrentIndex(startIndex);
  }, [startIndex]);

  useEffect(() => {
    if (!open || !currentStory) return;

    // Mark story as viewed
    onStoryViewed(currentStory.id);

    // Reset progress
    setProgress(0);
    setIsPlaying(true);
    setIsPaused(false);

    // Start progress timer
    const startTime = Date.now();
    
    const updateProgress = () => {
      if (!isPaused) {
        const elapsed = Date.now() - startTime;
        const progressPercent = Math.min((elapsed / storyDuration) * 100, 100);
        setProgress(progressPercent);

        if (progressPercent >= 100) {
          handleNext();
        }
      }
    };

    progressIntervalRef.current = setInterval(updateProgress, 50);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [currentIndex, open, isPaused]);

  const handleNext = () => {
    if (currentIndex < userStories.stories.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      onNext();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      onPrevious();
    }
  };

  const handlePauseToggle = () => {
    setIsPaused(!isPaused);
    
    if (currentStory?.content_type === 'video' && videoRef.current) {
      if (isPaused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  };

  const handleVideoEnded = () => {
    handleNext();
  };

  if (!currentStory) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-full h-[95vh] max-h-[800px] p-0 bg-black border-none overflow-hidden flex flex-col">
        <div className="relative w-full h-full flex flex-col">
          {/* Progress Bars */}
          <div className="absolute top-2 left-2 right-2 z-20 flex space-x-1">
            {userStories.stories.map((_, index) => (
              <div
                key={index}
                className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden"
              >
                <div
                  className="h-full bg-white transition-all duration-100 ease-linear"
                  style={{
                    width: `${
                      index < currentIndex
                        ? 100
                        : index === currentIndex
                        ? progress
                        : 0
                    }%`
                  }}
                />
              </div>
            ))}
          </div>

          {/* Header */}
          <div className="absolute top-8 left-2 right-2 z-20 flex items-center justify-between text-white">
            <div className="flex items-center space-x-3">
              <Avatar className="w-8 h-8">
                <AvatarImage src={userStories.avatar_url || undefined} />
                <AvatarFallback className="bg-gray-600 text-white text-sm">
                  {userStories.username.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium">
                  {userStories.display_name || userStories.username}
                </p>
                <p className="text-xs text-white/70">
                  {formatDistanceToNow(new Date(currentStory.created_at), {
                    addSuffix: true,
                    locale: it
                  })}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePauseToggle}
                className="text-white hover:bg-white/20 w-8 h-8 p-0"
              >
                {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Content Container */}
          <div className="flex-1 flex flex-col min-h-0 mt-16">
            {/* Media Content */}
            <div className="flex-1 relative min-h-0">
              {currentStory.content_type === 'video' ? (
                <video
                  ref={videoRef}
                  src={currentStory.content_url}
                  className="w-full h-full object-contain bg-black"
                  autoPlay
                  muted
                  onEnded={handleVideoEnded}
                  onClick={handlePauseToggle}
                />
              ) : (
                <img
                  src={currentStory.content_url}
                  alt="Story"
                  className="w-full h-full object-contain bg-black"
                  onClick={handlePauseToggle}
                />
              )}

              {/* Navigation Areas */}
              <button
                onClick={handlePrevious}
                className="absolute left-0 top-0 w-1/3 h-full z-10 bg-transparent"
              />
              <button
                onClick={handleNext}
                className="absolute right-0 top-0 w-1/3 h-full z-10 bg-transparent"
              />

              {/* Navigation Buttons (visible on hover) */}
              <div className="absolute inset-y-0 left-4 flex items-center opacity-0 hover:opacity-100 transition-opacity z-20">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handlePrevious}
                  className="text-white hover:bg-white/20 w-10 h-10 p-0 rounded-full"
                  disabled={currentIndex === 0}
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
              </div>
              
              <div className="absolute inset-y-0 right-4 flex items-center opacity-0 hover:opacity-100 transition-opacity z-20">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleNext}
                  className="text-white hover:bg-white/20 w-10 h-10 p-0 rounded-full"
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </div>
            </div>

            {/* Caption Area */}
            {currentStory.caption && (
              <div className="flex-shrink-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-white text-sm leading-relaxed">
                  {currentStory.caption}
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StoryViewModal;